import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Form, Input, Row, Table, Tooltip, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useLazyQuery } from '@apollo/client';
import TablePaginationBar from 'components/TablePaginationBar';
import {
    DISPATCH_EVENT_TASK_ORDER_HISTORIES,
    DispatchEventTaskOrderFilter,
    DispatchEventTaskOrderHistoriesResponse,
    DispatchEventTaskOrderItem,
} from '../graphql/queries/dispatchToolQueries';
import {
    DEFAULT_PAGINATION,
    PAGE_SIZE_OPTIONS,
    parseIdInput,
    renderDateTime,
    renderEventStatus,
    renderFlag,
    renderRetryCount,
} from './dispatchToolShared';

type FilterValues = {
    dispatchEventTaskId?: string;
    orderId?: string;
    bagId?: string;
};

type Props = {
    /** Tab1「查看明细」联动传入的调度事件任务ID（已归档任务） */
    linkedTaskId?: number;
    /** 联动触发序号（同一 ID 连续点击也要重新触发查询） */
    linkSeq?: number;
};

/** 调度任务订单归档表（tms_dispatch_event_task_order_history）查询，只读无更新操作 */
const DispatchEventTaskOrderHistoryTab: React.FC<Props> = ({ linkedTaskId, linkSeq }) => {
    const [form] = Form.useForm<FilterValues>();
    // 首挂载若由联动进入，初始条件即联动任务ID，避免先按默认条件多查一次
    const [activeFilter, setActiveFilter] = useState<DispatchEventTaskOrderFilter>(() =>
        linkedTaskId !== undefined ? { dispatchEventTaskId: linkedTaskId } : {},
    );
    const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
    // 归档表数据量大，默认不查；仅在点击「查询」或 Tab1 联动进入后才发起请求
    const [searched, setSearched] = useState(linkedTaskId !== undefined);
    const handledSeq = useRef(linkSeq);
    const [runQuery, { data, loading }] = useLazyQuery<DispatchEventTaskOrderHistoriesResponse>(
        DISPATCH_EVENT_TASK_ORDER_HISTORIES,
        {
            fetchPolicy: 'network-only',
            onError: (e) => message.error(e.message),
        },
    );

    useEffect(() => {
        if (!searched) return;
        runQuery({
            variables: {
                filter: activeFilter,
                pageRequest: { page: pagination.current, size: pagination.pageSize },
            },
        });
    }, [searched, activeFilter, pagination, runQuery]);

    useEffect(() => {
        if (linkedTaskId === undefined || linkSeq === undefined) return;
        if (handledSeq.current === linkSeq) return;
        handledSeq.current = linkSeq;
        form.setFieldsValue({
            dispatchEventTaskId: String(linkedTaskId),
            orderId: undefined,
            bagId: undefined,
        });
        setPagination((prev) => ({ ...prev, current: 1 }));
        setActiveFilter({ dispatchEventTaskId: linkedTaskId });
        setSearched(true);
    }, [linkedTaskId, linkSeq, form]);

    const handleSearch = () => {
        const values = form.getFieldsValue();
        const dispatchEventTaskId = parseIdInput(values.dispatchEventTaskId);
        const orderId = parseIdInput(values.orderId);
        const bagId = parseIdInput(values.bagId);
        if (dispatchEventTaskId === null || orderId === null || bagId === null) {
            message.warning('ID 必须是纯数字');
            return;
        }
        // 归档表数据量大，禁止无条件全表分页查询
        if (dispatchEventTaskId === undefined && orderId === undefined && bagId === undefined) {
            message.warning('调度任务ID、订单ID、包牌ID 至少填写一个');
            return;
        }
        setPagination((prev) => ({ ...prev, current: 1 }));
        setActiveFilter({
            dispatchEventTaskId,
            orderId,
            bagId,
        });
        setSearched(true);
    };

    const columns: ColumnsType<DispatchEventTaskOrderItem> = [
        { title: 'ID', dataIndex: 'id', width: 90 },
        { title: '调度任务ID', dataIndex: 'dispatchEventTaskId', width: 100 },
        { title: '任务ID', dataIndex: 'taskId', width: 90 },
        { title: '途经点ID', dataIndex: 'taskWaypointId', width: 100 },
        { title: '订单ID', dataIndex: 'orderId', width: 100 },
        { title: '包牌ID', dataIndex: 'bagId', width: 90, render: (v) => v ?? '-' },
        { title: '发车', dataIndex: 'departureEventStatus', width: 90, render: renderEventStatus },
        { title: '到达', dataIndex: 'arrivedEventStatus', width: 90, render: renderEventStatus },
        { title: 'Handover', dataIndex: 'handoverEventStatus', width: 90, render: renderEventStatus },
        { title: 'Tender', dataIndex: 'tenderEventStatus', width: 90, render: renderEventStatus },
        { title: '卸车', dataIndex: 'unloadingEventStatus', width: 90, render: renderEventStatus },
        { title: 'LogScan', dataIndex: 'logscanEventStatus', width: 90, render: renderEventStatus },
        { title: '装车移出', dataIndex: 'removalEventStatus', width: 90, render: renderEventStatus },
        { title: '已完成', dataIndex: 'isCompleted', width: 80, render: (v: number) => renderFlag(v) },
        { title: '已发MQ', dataIndex: 'isSentMq', width: 80, render: (v: number) => renderFlag(v) },
        { title: '重试次数', dataIndex: 'retryCount', width: 90, render: renderRetryCount },
        {
            title: '异常信息',
            dataIndex: 'exceptionMsg',
            width: 200,
            ellipsis: { showTitle: false },
            render: (v: string | null) =>
                v ? (
                    <Tooltip title={v} placement="topLeft">
                        <span>{v}</span>
                    </Tooltip>
                ) : (
                    '-'
                ),
        },
        { title: '创建时间', dataIndex: 'createdAt', width: 160, render: renderDateTime },
        { title: '更新时间', dataIndex: 'updatedAt', width: 160, render: renderDateTime },
    ];

    const rows = data?.dispatchEventTaskOrderHistories.content ?? [];
    const totalElements = data?.dispatchEventTaskOrderHistories.totalElements ?? 0;

    return (
        <div>
            <Form
                form={form}
                initialValues={{
                    dispatchEventTaskId: linkedTaskId !== undefined ? String(linkedTaskId) : undefined,
                }}
            >
                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item name="dispatchEventTaskId" label="调度任务ID">
                            <Input allowClear placeholder="精确匹配" />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="orderId" label="订单ID">
                            <Input allowClear placeholder="精确匹配" />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="bagId" label="包牌ID">
                            <Input allowClear placeholder="精确匹配" />
                        </Form.Item>
                    </Col>
                    <Col span={8} className="ml-auto text-right">
                        <Form.Item>
                            <Button className="mr-2" onClick={() => form.resetFields()}>
                                重置
                            </Button>
                            <Button type="primary" loading={loading} onClick={handleSearch}>
                                查询
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
            <Table<DispatchEventTaskOrderItem>
                rowKey="id"
                size="small"
                loading={loading}
                columns={columns}
                dataSource={rows}
                pagination={false}
                scroll={{ x: 1790 }}
                className="mb-4"
            />
            <TablePaginationBar
                total={totalElements}
                current={pagination.current}
                pageSize={pagination.pageSize}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                onChange={(page, size) => setPagination({ current: page, pageSize: size })}
            />
        </div>
    );
};

export default DispatchEventTaskOrderHistoryTab;
